import React, { useState, useEffect, useRef } from "react";
import GenericHeader from "../../../components/generic/genericHeader";
import { Layout, message, Space } from "antd";
import { backendGetRequest, backendGetRequestWithCache } from "../../../helpers/callBackend";
import AnalyticsFilters from "./filters";
import SimpleArea from "../../../components/charts/area";
import { toPrice } from "../../../helpers/stringFormat";
import { addDays, addWeeks, addMonths, addYears, subDays, subWeeks, subMonths, subYears, startOfMonth, startOfQuarter, startOfYear, getDay, formatISO, max } from 'date-fns';
import TitledSection from "../../../components/generic/titledSection";
import TableWithLoadingWrapper from "../../../components/generic/dataGrid";
import OuterBox from "../../../components/generic/arpariBoxes/OuterBox";
import { groupingTypes } from "../reports/constants";
 
function Analytics() {
    const cadenceFilter = ["Daily", "Weekly", "Monthly", "Quarterly", "Annually"];
    const includePeriodToDateFilter = ["Yes", "No"];
    const accountGroupingFilter = [
        "Bank",
        "Entity",
        "Account"
    ];
    const accountGroupingToApiMapping = {
        "Bank": "BA",
        "Entity": "EN", 
        "Account": "AA"
    };
    const [selectGroupFilter, setSelectGroupFilter] = useState([]);
    const [runAnalyticsButtonLoading, setRunAnalyticsButtonLoading] = useState(false);
    const [accountGrouping, setAccountGrouping] = useState(null);
    const [selectGroup, setSelectGroup] = useState(null);
    const [chartData, setChartData] = useState([]);
    const [tableData, setTableData] = useState([]);
    const [exportData, setExportData] = useState(null);
    const [runValues, setRunValues] = useState(null);
    const [canvasUrl, setCanvasUrl] = useState(null);
    const pdfDownloadLinkRef = useRef(null);
    const [groupingOptionsCache, setGroupingOptionsCache] = useState({});
    const [loadingGroupingOptions, setLoadingGroupingOptions] = useState({ 'BA': true, 'PU': true, 'EN': true, 'HI': true, 'AA': true });

    const columns = [
        {
            field: 'account',
            headerName: '',
            flex: 1.5,
            sortable: true,
            sortComparator: (v1, v2) => v1.localeCompare(v2),
        },
        {
            field: 'starting_balance',
            headerName: 'Starting Balance',
            flex: 1,
            sortable: true,
            sortComparator: (v1, v2) => v1 - v2,
            renderCell: (params) => (
                <span>
                    {toPrice(params.value)}
                </span>
            ),
        },
        {
            field: 'ending_balance',
            headerName: 'Ending Balance',
            flex: 1,
            sortable: true,
            sortComparator: (v1, v2) => v1 - v2,
            renderCell: (params) => (
                <span>
                    {toPrice(params.value)}
                </span>
            ),
        },
        {
            field: 'transactions',
            headerName: 'Transactions',
            flex: 1,
            sortable: true,
            sortComparator: (v1, v2) => v1 - v2,
            renderCell: (params) => (
                <span>
                    {toPrice(params.value)}
                </span>
            ),
        },
    ];

    useEffect(() => {
        if (!accountGrouping) {
            setSelectGroupFilter([]);
            setSelectGroup(null);
            return;
        }
        const apiGroupingType = accountGroupingToApiMapping[accountGrouping];
        if (groupingOptionsCache[apiGroupingType]) {
            setSelectGroupFilter(groupingOptionsCache[apiGroupingType]);
        } else {
            setSelectGroupFilter([]);
        }
        
        setSelectGroup(null);
    }, [accountGrouping, groupingOptionsCache]);

    useEffect(() => {
        groupingTypes.map((type) => {
          if (!groupingOptionsCache[type]) {
            setLoadingGroupingOptions(prev => ({ ...prev, [type]: true }));
            try {
                backendGetRequestWithCache(
                "/api/landlord/grouping-type-filter-options/",
                { params: { grouping_type: type } },
                (cachedResponse) => {
                  if (cachedResponse?.data?.data && !groupingOptionsCache[type]) {
                    setGroupingOptionsCache(prev => ({ ...prev, [type]: cachedResponse.data.data }));
                  }
                },
                (nonCachedResponse) => {
                  if (nonCachedResponse?.data?.data) {
                    setGroupingOptionsCache(prev => ({ ...prev, [type]: nonCachedResponse.data.data }));
                  }
                },
                (error) => {
                  console.error(`Error preloading grouping options for ${type}:`, error);
                }
              );
            } catch (error) {
              console.error(`Error preloading grouping options for ${type}:`, error);
            } finally {
              setLoadingGroupingOptions(prev => ({ ...prev, [type]: false }));
            }
          }
        });
    }, []);

    useEffect(() => {
        return () => {
            setLoadingGroupingOptions({});
        };
    }, []);

    const getAnalyticsParams = (accountGrouping, selectGroup) => {
        // Return parameters for analytics API based on grouping type and selected group
        const apiGroupingType = accountGroupingToApiMapping[accountGrouping];
        return {
            grouping_type: apiGroupingType,
            group_id: selectGroup
        };
    }

    const updateChartDataWithNewAccounts = (snapshots) => {
        setChartData(prevState => {
            let newChartData = [...prevState];
            snapshots.forEach((snapshot) => {
                const index = newChartData.findIndex(data => data.x === snapshot.date.substring(0, 10));
                if (index !== -1) {
                    // If date exists in newChartData, add balance to y
                    newChartData[index].y += snapshot.balance;
                } else {
                    // If date doesn't exist in newChartData, add a new entry
                    newChartData.push({ x: snapshot.date.substring(0, 10), y: snapshot.balance });
                }
            });
            newChartData.sort((a, b) => new Date(a.x) - new Date(b.x));
            return newChartData;
        });
    }

    const calculateNewBalancesAndTransactions = (snapshotArr) => ({
        starting_date: snapshotArr[0].date.substring(0, 10),
        ending_date: snapshotArr[snapshotArr.length - 1].date.substring(0, 10),
        starting_balance: snapshotArr[0].balance,
        ending_balance: snapshotArr[snapshotArr.length - 1].balance,
        transactions: snapshotArr.reduce((total, snapshot) => total + snapshot.transaction_inflow - snapshot.transaction_outflow, 0) || 0
    });

    const calculateUpdateBalancesAndTransactions = (snapshotArr, prevRowData) => {

        const snapshotStartDate = new Date(snapshotArr[0].date.substring(0, 10));
        const snapshotEndDate = new Date(snapshotArr[snapshotArr.length - 1].date.substring(0, 10));
        const prevStartDate = new Date(prevRowData.starting_date);
        const prevEndDate = new Date(prevRowData.ending_date);
        let starting_date, starting_balance, ending_date, ending_balance, transactions;
        if (snapshotEndDate < prevStartDate) {
            // snapshot ends before the previous state timeframe starts
            starting_date = snapshotArr[0].date.substring(0, 10);
            starting_balance = snapshotArr[0].balance;
            ending_date = prevRowData.ending_date.substring(0, 10);
            ending_balance = prevRowData.ending_balance;
        } else if (snapshotStartDate > prevEndDate) {
            // snapshot starts after the previous state timeframe ends
            starting_date = prevRowData.starting_date.substring(0, 10);
            starting_balance = prevRowData.starting_balance;
            ending_date = snapshotArr[snapshotArr.length - 1].date.substring(0, 10);
            ending_balance = snapshotArr[snapshotArr.length - 1].balance;
        } else {
            // should not occur since overlapping dates are impossible
            throw new Error("Overlapping dates detected");
        }
        transactions = prevRowData.transactions + snapshotArr.reduce((total, snapshot) => total + snapshot.transaction_inflow - snapshot.transaction_outflow, 0) || 0;
        return {
            starting_date,
            starting_balance,
            ending_date,
            ending_balance,
            transactions
        };
    };

    const updateTableDataWithNewAccounts = (snapshots) => {
        const findIndex = (data) => data.key === snapshots[0].bank_account.id;
        const sortByDate = (a, b) => new Date(a.date) - new Date(b.date);
        const sortedSnapshots = snapshots.sort(sortByDate);
        setTableData((prevState) => {
            const index = prevState.findIndex(findIndex);
            let newState;
            if (index !== -1) {
                const updatedTableData = {
                    key: snapshots[0].bank_account.id,
                    account: snapshots[0].bank_account.descriptor,
                    ...calculateUpdateBalancesAndTransactions(sortedSnapshots, prevState[index])
                };
                // If the bank account exists in the previous state, update it
                newState = prevState.map((item, idx) => idx === index ? updatedTableData : item);
            } else {
                // If the bank account doesn't exist, add it to the state
                const updatedTableData = {
                    key: snapshots[0].bank_account.id,
                    account: snapshots[0].bank_account.descriptor,
                    ...calculateNewBalancesAndTransactions(sortedSnapshots)
                };
                newState = [...prevState, updatedTableData];
            }
            return newState.sort(sortByDate);
        });
    }

    const updateEndDate = (startDate, numPeriods, cadence) => {
        const cadenceParams = {
            'Daily': 'days',
            'Weekly': 'weeks',
            'Monthly': 'months',
            'Quarterly': 'months',
            'Annually': 'years'
        };
        const kwargsKey = cadenceParams[cadence];
        if (kwargsKey === 'days') {
            return addDays(startDate, numPeriods);
        } else if (kwargsKey === 'weeks') {
            return addWeeks(startDate, numPeriods);
        } else if (kwargsKey === 'months') {
            return addMonths(startDate, numPeriods);
        } else if (kwargsKey === 'Quarterly') {
            return addMonths(startDate, numPeriods * 3)
        } else if (kwargsKey === 'years') {
            return addYears(startDate, numPeriods);
        } else {
            throw new Error('Invalid cadence');
        }
    }

    const calculateDateRange = (cadence, numPeriods, includePtd, endDate) => {
        endDate = new Date(endDate);
        const cadenceDict = {
            'Daily': (ed, np) => subDays(ed, np),
            'Weekly': (ed, np) => subDays(subWeeks(ed, np), getDay(endDate)),
            'Monthly': (ed, np) => startOfMonth(subMonths(ed, np)),
            'Quarterly': (ed, np) => subMonths(startOfQuarter(ed), np * 3),
            'Annually': (ed, np) => subYears(startOfYear(ed), np)
        };
        if (!cadenceDict.hasOwnProperty(cadence)) {
            message.error("Invalid Cadence");
        }
        let startDate = cadenceDict[cadence](endDate, Number(numPeriods));
        startDate = new Date(startDate);
        if (includePtd === 'No') {
            endDate = updateEndDate(startDate, numPeriods, cadence);
        }
        return {
            'start_date': startDate,
            'end_date': endDate
        };
    }

    const handleRunAnalysis = async (values) => {
        setRunValues(values);
        setRunAnalyticsButtonLoading(true);
        setChartData([]);
        setTableData([]);
        
        const analyticsParams = getAnalyticsParams(values.accountGrouping, values.selectGroup);
        const interval = calculateDateRange(values.cadence, values.numberOfPeriods, values.includePeriodToDate, values.endDate);

        const startDate = formatISO(interval.start_date, { format: 'basic' });
        const endDate = formatISO(interval.end_date, { format: 'basic' });

        try {
            const response = await backendGetRequest("/api/analytics/", {
                params: {
                    ...analyticsParams,
                    start_date: startDate,
                    end_date: endDate,
                }
            });

            if (response?.data?.data) {
                updateChartDataWithNewAccounts(response.data.data);
                updateTableDataWithNewAccounts(response.data.data);
            }
        } catch (error) {
            console.error("Error running analytics:", error);
            message.error("Failed to run analytics");
        } finally {
            setRunAnalyticsButtonLoading(false);
        }
    }

    const updateCanvasUrl = () => {
        const canvasElement = document.querySelector('canvas');
        if (canvasElement) {
            const dataURL = canvasElement.toDataURL("image/png");
            setCanvasUrl(dataURL);
        }
    }

    const handleExportPDF = () => {
        let _tableData = tableData;
        let newExportData = { 'title': 'Analytics', 'tableData': '' };
        if (runValues) {
            let selectedGroup = selectGroupFilter.find(item => item.id === runValues.selectGroup);
            selectedGroup = selectedGroup?.name ? selectedGroup.name : runValues.selectGroup;
            const interval = calculateDateRange(runValues.cadence, runValues.numberOfPeriods, runValues.includePeriodToDate, runValues.endDate);
            let startDate = interval.start_date.toDateString();
            let endDate = interval.end_date.toDateString();
            newExportData['title'] = `Analysis of ${runValues.accountGrouping} ${selectedGroup} from ${startDate} to ${endDate}, Run ${runValues.cadence}${runValues.includePeriodToDate === "Yes" ? ", including Period to Date" : ""}`;
        }
        updateCanvasUrl();
        newExportData['tableData'] = _tableData;
        setExportData(newExportData);
        pdfDownloadLinkRef.current.click();
    }

    return (
        <OuterBox>
            <AnalyticsFilters
                cadenceFilter={cadenceFilter}
                includePeriodToDateFilter={includePeriodToDateFilter}
                accountGroupingFilter={accountGroupingFilter}
                selectGroupFilter={selectGroupFilter}
                accountGrouping={accountGrouping}
                selectGroup={selectGroup}
                setAccountGrouping={setAccountGrouping}
                setSelectGroup={setSelectGroup}
                handleRunAnalysis={handleRunAnalysis}
                runAnalyticsButtonLoading={runAnalyticsButtonLoading}
                handleExportPDF={handleExportPDF}
                exportData={exportData}
                exportFileName={"analytics.pdf"}
                exportPDFButtonLoading={!tableData || tableData.length === 0}
                pdfDownloadLinkRef={pdfDownloadLinkRef}
                canvasUrl={canvasUrl}
                loadingGroupingOptions={!accountGrouping || loadingGroupingOptions[accountGroupingToApiMapping[accountGrouping]]}
            />
            <TitledSection
                title="Cash Balance Trends"
                titleExtra={undefined}
                body={<SimpleArea areachartdata={chartData} />}
            />
            <TitledSection
                title="Accounts"
                titleExtra={undefined}
                body={<TableWithLoadingWrapper
                    style={{ boxShadow: 'none', width: "100%" }}
                    columns={columns}
                    dataSource={tableData}
                    pagination={{ defaultPageSize: 50, showSizeChanger: true }}
                    getRowId={row => row?.key}
                />}
            />
        </OuterBox>
    );
}

export default Analytics;
